import type {CSSProperties} from 'react';
import {font, radius, space, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeTesterLook = 'tester-tab' | 'tester-tab-on' | 'tester-tier' | 'tester-tier-on' | 'tester-reset' | 'tester-reset-red' | 'tester-danger' | 'tester-cancel' | 'tester-refresh';

const TESTER_LOOKS = new Set<string>(['tester-tab', 'tester-tab-on', 'tester-tier', 'tester-tier-on', 'tester-reset', 'tester-reset-red', 'tester-danger', 'tester-cancel', 'tester-refresh']);
export const isWedgeTesterLook = (look: string): look is WedgeTesterLook => TESTER_LOOKS.has(look);

/* Released Tester Tools (Settings, founder-testers only): six kinds of native control. The tab is one of four in a grid strip that the
   caller draws. It is a 10px radius box with 8px by 0 padding, 12.5px bold type, a 5px gap and a centered flex row for its glyph and word;
   idle it is bare with the secondary ink, chosen it takes the surface fill and the label ink. The tier row and the reset row are full-width
   left-aligned flex columns with a 3px gap, 10px by 12px padding and a 10px radius. The tier row has a hairline 0.5px border: elevated fill
   and separator idle, soft accent tint and accent chosen. The reset row has a 1px card border on the elevated fill, in the label ink, or the
   red ink for `tester-reset-red`. Both rows take the wait cursor while the caller holds them with `disabled`, and keep every other value.
   The smoke test button is a full-width 10px radius block with 10px by 12px padding, 13px bold type, the soft red fill, the red ink and a
   0.5px red border. Cancel in the reset confirmation is a bare full-width block with 10px of padding and 14px medium type in the secondary
   ink; held it takes the wait cursor. The Refresh button of the System health card is a bare text action: no fill and no border, the accent
   ink, 12px semibold type and 4px of padding on every side; held (the caller sets `disabled` while a read runs) it takes the wait cursor.
   Each reads the exact legacy roles, so the shipped dark ink rules keep matching the serialized red ink and the shipped light rules the
   accent ink after a semicolon, and the declaration order is the order of the source. The caller owns the glyph, the labels, the handlers
   and the layout. */
const TAB_PADDING = '8px 0', TAB_FONT = 12.5, TAB_GAP = 5;
const ROW_PADDING = '10px 12px', ROW_GAP = 3, HAIRLINE = 0.5;

export function wedgeTesterStyle(look: WedgeTesterLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'tester-tab' || look === 'tester-tab-on') {
    const on = look === 'tester-tab-on';
    return {
      padding: TAB_PADDING, border: 0, cursor: 'pointer', borderRadius: radius.md,
      background: on ? r('surface') : 'transparent', color: on ? r('label') : r('labelSec'),
      fontSize: TAB_FONT, fontWeight: weight.bold, fontFamily: r('font'),
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: TAB_GAP,
    };
  }
  if (look === 'tester-tier' || look === 'tester-tier-on') {
    const on = look === 'tester-tier-on';
    return {
      textAlign: 'left', display: 'flex', flexDirection: 'column', gap: ROW_GAP,
      padding: ROW_PADDING, borderRadius: radius.md,
      background: on ? r('accentTint') : r('bgElev'),
      border: `${HAIRLINE}px solid ${r(on ? 'accent' : 'separator')}`,
      color: r('label'), cursor: disabled ? 'wait' : 'pointer', fontFamily: r('font'),
    };
  }
  if (look === 'tester-reset' || look === 'tester-reset-red') return {
    textAlign: 'left', display: 'flex', flexDirection: 'column', gap: ROW_GAP,
    padding: ROW_PADDING, borderRadius: radius.md,
    background: r('bgElev'), border: `1px solid ${r('cardBorder')}`,
    color: look === 'tester-reset-red' ? r('red') : r('label'), cursor: disabled ? 'wait' : 'pointer', fontFamily: r('font'),
  };
  if (look === 'tester-danger') return {
    width: '100%', padding: ROW_PADDING, borderRadius: radius.md,
    background: r('redSoft'), color: r('red'), border: `${HAIRLINE}px solid ${r('red')}`,
    fontSize: font.footnote, fontWeight: weight.bold, cursor: 'pointer', fontFamily: r('font'),
  };
  if (look === 'tester-refresh') return {
    background: 'none', border: 0, color: r('accent'), fontSize: font.caption, fontWeight: weight.semibold,
    cursor: disabled ? 'wait' : 'pointer', padding: space[1], fontFamily: r('font'),
  };
  return {
    display: 'block', width: '100%', background: 'none', border: 0, color: r('labelSec'),
    fontSize: font.body, fontWeight: weight.medium, padding: 10, cursor: disabled ? 'wait' : 'pointer', fontFamily: r('font'),
  };
}
